import { WorkStatus, WorkStatusCategory } from "types/project/projectDefaults";
import React, { useMemo } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import { Menu, MenuButton, MenuItem, MenuItems, Portal } from '@headlessui/react';

export const workStatusUIMap: Record<
  WorkStatusCategory,
  { bg: string; text: string; dot: string; border: string; color: string }
> = {
  TODO: {
    bg: 'bg-[#3B82F622]',
    text: 'text-[#60A5FA]',
    dot: 'bg-[#3B82F6]',
    border: 'border-[#3B82F644]',
    color: 'bg-[#3B82F633] text-[#60A5FA]',
  },
  IN_PROGRESS: {
    bg: 'bg-[#FBBF2422]',
    text: 'text-[#FBBF24]',
    dot: 'bg-[#FBBF24]',
    border: 'border-[#FBBF2444]',
    color: 'bg-[#FDE68A22] text-[#FBBF24]',
  },
  DONE: {
    bg: 'bg-[#10B98122]',
    text: 'text-[#34D399]',
    dot: 'bg-[#10B981]',
    border: 'border-[#10B98144]',
    color: 'bg-[#10B98133] text-[#34D399]',
  },
};

interface StatusDropdownProps {
  workStatuses: WorkStatus[];
  currentStatusUuid: string;
  onStatusChange: (status: WorkStatus) => void;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const sizeClasses = {
  sm: 'px-2 py-1 text-xs',
  md: 'px-3 py-1 text-xs',
  lg: 'px-4 py-2 text-sm',
};

const StatusDropdown: React.FC<StatusDropdownProps> = ({
  workStatuses,
  currentStatusUuid,
  onStatusChange,
  disabled = false,
  size = 'md',
  className = '',
}) => {
  const currentStatus = useMemo(
    () => workStatuses.find((s) => s.workStatusUuid === currentStatusUuid),
    [workStatuses, currentStatusUuid]
  );

  if (!currentStatus) return null;

  const ui = workStatusUIMap[currentStatus.workStatusCategory];

  return (
    <Menu as="div" className="relative block w-full text-left">
      <MenuButton
        type="button"
        disabled={disabled}
        className={`${sizeClasses[size]} rounded-full font-medium flex items-center gap-2 border ${ui.bg} ${ui.border} ${ui.text} hover:brightness-110 transition-all duration-200 disabled:opacity-50 group ${className}`}
      >
        <span className="truncate">{currentStatus.name}</span>
        <ChevronDownIcon className="w-3 h-3 transition-opacity flex-shrink-0" />
      </MenuButton>

      <Portal>
        <MenuItems
          transition
          anchor="bottom start"
          className="mt-2 z-[9999] rounded-lg bg-primary-variant border border-primary-subtle shadow-lg focus:outline-none focus:ring-0"
        >
          {/* Changed to a vertical flex column, removed the header, and reduced the width */}
          <div className="p-2 min-w-[200px] flex flex-col gap-1.5">
            {workStatuses.map((status) => {
              const statusUI = workStatusUIMap[status.workStatusCategory];
              const isSelected = status.workStatusUuid === currentStatusUuid;

              return (
                <MenuItem key={status.workStatusUuid}>
                  {({ focus }) => (
                    <button
                      onClick={() => onStatusChange(status)}
                      className={`w-full text-left px-3 py-2 rounded text-xs font-medium transition-all
                        ${statusUI.color}
                        ${
                          isSelected
                            ? 'ring-2 ring-blue-400'
                            : ''
                        }
                        ${focus ? 'opacity-80' : 'hover:opacity-80'}`}
                    >
                      <span className="truncate">{status.name}</span>
                    </button>
                  )}
                </MenuItem>
              );
            })}
          </div>
        </MenuItems>
      </Portal>
    </Menu>
  );
};

export default StatusDropdown;